import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { useLang } from "../../lib/lang";
import { waLink } from "../../lib/content";
import { EASE } from "./Reveal";

const MaskLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const TiltPoster = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const gx = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
  const glare = useTransform(gx, (v) => `radial-gradient(circle at ${v} 30%, rgba(255,255,255,0.22), transparent 55%)`);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} ref={ref}>
      <motion.div
        data-testid="hero-mascot-card"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92, rotateZ: -4 }}
        animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
        transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
        className="relative aspect-[4/5] w-full rounded-[28px] overflow-hidden border border-violet-400/40 shadow-[0_0_80px_rgba(139,92,246,0.45)]"
      >
        <img src="/img/poster.webp" alt="Vinslab Maker red panda mascot" className="absolute inset-0 w-full h-full object-cover object-[50%_85%] scale-[1.2] origin-bottom" />
        <motion.div className="absolute inset-0 pointer-events-none" style={{ background: glare }} />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0E1B]/90 to-transparent" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <span className="font-script text-2xl text-white">Make It Real.</span>
          <span className="font-mono text-[11px] text-sky-200 tracking-widest">VINSLAB MAKER</span>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] pt-28 pb-16 lg:pt-32 overflow-hidden hex-bg">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-violet-600/30 blur-[140px]" />
      <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-[#FF2E4D]/20 blur-[140px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex flex-wrap items-center gap-3 mb-8">
            <span data-testid="hero-status-badge" className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t.heroBadge}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-300">{t.heroEyebrow}</span>
          </motion.div>
          <h1 data-testid="hero-title" className="font-display uppercase leading-[0.86] tracking-[-0.03em] text-[12.5vw] sm:text-[11.5vw] lg:text-[7vw] xl:text-[104px]">
            <MaskLine delay={0.15} className="text-white neon-text">{t.heroLines[0]}</MaskLine>
            <MaskLine delay={0.3} className="text-outline">{t.heroLines[1]}</MaskLine>
          </h1>
          <div className="overflow-hidden mt-3">
            <motion.p initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.5, ease: EASE }} className="font-script text-3xl sm:text-4xl text-sky-300 -rotate-2 origin-left">
              {t.heroScript}
            </motion.p>
          </div>
          <motion.p key={t.heroSub} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-8 max-w-xl text-base lg:text-lg text-slate-300 leading-relaxed">
            {t.heroSub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }} className="mt-10 flex flex-wrap gap-4">
            <a href={waLink(t.waGreeting)} target="_blank" rel="noreferrer" data-testid="hero-order-button" className="group inline-flex items-center gap-3 rounded-full bg-[#FF2E4D] pl-6 pr-2 py-2 font-semibold text-white shadow-[0_0_40px_rgba(255,46,77,0.5)] hover:shadow-[0_0_60px_rgba(255,46,77,0.75)] transition-shadow duration-300">
              {t.heroCta}
              <span className="grid place-items-center w-10 h-10 rounded-full bg-white text-[#FF2E4D] transition-transform duration-300 group-hover:rotate-[-20deg]"><MessageCircle className="w-5 h-5" /></span>
            </a>
            <a href="#work" data-testid="hero-work-button" className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-4 text-white hover:border-sky-300 hover:text-sky-200 transition-colors duration-300">
              {t.heroCta2} <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yCard }} className="lg:col-span-5 relative max-w-md mx-auto w-full lg:max-w-none">
          <div className="absolute -inset-6 rounded-[40px] border border-sky-400/30 animate-[spin_40s_linear_infinite] [border-style:dashed]" />
          <TiltPoster />
        </motion.div>
      </div>
    </section>
  );
};
